/* ===================== PASSWORD & SECURITY (own card) =====================
   Sits under Account Settings as a separate surface — profile edits and a
   password re-key are different acts, and mixing them into one Save button
   would make an accidental password change one stray click away.

   POST /change-password (auth BFF) with { current_password, password,
   password_confirmation }. Upstream is Laravel's logoutOtherDevices(): every
   token issued before the change is revoked, and the replacement it returns
   is re-sealed into the session cookie by the BFF — which is why this goes
   through @astro/auth rather than the /api proxy, and why the user stays
   signed in here while other devices drop out.

   422s come back as per-field messages under `details` (current_password /
   password), keyed exactly like the payload, so they surface inline. */
import { useState } from 'react'
import { useAuth } from '@astro/auth'
import { useForm } from '@astro/forms'

const inputCls =
  'w-full bg-stone-50 border rounded-xl px-4 py-3 pr-11 text-sm text-astroDark focus:outline-none focus:border-goldAccent/50 focus:bg-white transition-colors'

/** Eye / eye-off, inline so the card carries no icon-package dependency. */
function EyeIcon({ off }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
      {off && <path d="M4 20 20 4" />}
    </svg>
  )
}

/** Password input with its own reveal toggle and inline error slot. */
function PasswordField({ label, error, register, autoComplete, hint }) {
  const [visible, setVisible] = useState(false)
  return (
    <div>
      <label className="block text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          className={`${inputCls} ${error ? 'border-red-300' : 'border-stone-200'}`}
          {...register}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
          className="absolute inset-y-0 right-0 px-3.5 flex items-center text-stone-400 hover:text-goldAccent transition-colors outline-none focus:text-goldAccent"
        >
          <EyeIcon off={visible} />
        </button>
      </div>
      {error ? (
        <p className="text-[10px] text-red-500 mt-1">{error}</p>
      ) : hint ? (
        <p className="text-[10px] text-stone-400 mt-1">{hint}</p>
      ) : null}
    </div>
  )
}

const EMPTY = { current_password: '', password: '', password_confirmation: '' }

export default function ChangePasswordCard() {
  const { changePassword } = useAuth()
  const [banner, setBanner] = useState(null) // { tone: 'ok' | 'err', text }
  const [submitting, setSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    setError,
    getValues,
    formState: { errors },
  } = useForm({ defaultValues: EMPTY, mode: 'onTouched' })

  const onSubmit = handleSubmit(
    async (values) => {
      setBanner(null)
      setSubmitting(true)
      try {
        const result = await changePassword(values)
        reset(EMPTY)
        setBanner({
          tone: 'ok',
          text: result?.message || 'Password changed. You are still signed in on this device.',
        })
      } catch (err) {
        // Field-level 422s (wrong current password, too-short new one) land on
        // their own input; anything else stays a single line above the button.
        const details = err?.error?.details
        let inlined = false
        if (details && typeof details === 'object') {
          for (const [key, msgs] of Object.entries(details)) {
            if (key in EMPTY) {
              setError(key, { type: 'server', message: Array.isArray(msgs) ? msgs[0] : String(msgs) })
              inlined = true
            }
          }
        }
        setBanner({
          tone: 'err',
          text: inlined
            ? 'Please fix the highlighted fields.'
            : err?.error?.message || 'Could not change your password. Please try again.',
        })
      } finally {
        setSubmitting(false)
      }
    },
    () => setBanner({ tone: 'err', text: 'Please fix the highlighted fields.' }),
  )

  return (
    <div className="dash-card p-4 sm:p-6 mt-6">
      <h3 className="text-xl text-astroDark mb-1 font-royal">Password &amp; Security</h3>
      <p className="text-xs text-stone-500 mb-6">
        Choose a new password for your account. Changing it signs you out everywhere else — this device stays signed in.
      </p>

      <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" noValidate>
        <PasswordField
          label="Current Password"
          autoComplete="current-password"
          error={errors.current_password?.message}
          register={register('current_password', { required: 'Enter your current password' })}
        />
        {/* Spacer keeps the current-password field on a row of its own so it
            never reads as one of the two new-password boxes. */}
        <div className="hidden sm:block" />
        <PasswordField
          label="New Password"
          autoComplete="new-password"
          hint="At least 8 characters."
          error={errors.password?.message}
          register={register('password', {
            required: 'Enter a new password',
            minLength: { value: 8, message: 'At least 8 characters' },
            validate: (v) =>
              v !== getValues('current_password') || 'Choose a password different from your current one',
          })}
        />
        <PasswordField
          label="Confirm New Password"
          autoComplete="new-password"
          error={errors.password_confirmation?.message}
          register={register('password_confirmation', {
            required: 'Re-enter the new password',
            validate: (v) => v === getValues('password') || 'Passwords do not match',
          })}
        />

        <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t border-stone-100">
          <div className="min-w-0">
            {banner ? (
              <p className={`text-xs font-semibold ${banner.tone === 'ok' ? 'text-green-600' : 'text-red-500'}`}>{banner.text}</p>
            ) : (
              <p className="text-xs text-stone-500">Use 8+ characters with letters, numbers &amp; symbols.</p>
            )}
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="shine-btn bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[11px] uppercase tracking-[0.2em] px-6 py-3 rounded-xl shadow-md shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 shrink-0"
          >
            {submitting ? 'Updating…' : 'Change Password'}
          </button>
        </div>
      </form>
    </div>
  )
}
